import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import Modal from './Modal';
import FullScreenPanel from './FullScreenPanel';
import RequestDetail from './RequestDetail';
import LookupSuggest, { MetadataCard } from './LookupSuggest';
import SearchableSelect from './SearchableSelect';
import { Loader2, ClipboardList, FileText, Bell, Search, ChevronRight } from 'lucide-react';
import { formatLongDate, toDateInputValue, toDateTimeInputValue } from '../utils/datetime';
import { preventImplicitSubmit } from '../utils/form';

export default function EmployeeDashboard({ user, onNavigate }) {
  const [forms, setForms] = useState([]);
  const [formSearch, setFormSearch] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uniqueValidation, setUniqueValidation] = useState({});
  const uniqueDebounceTimersRef = useRef({});
  const uniqueRequestVersionRef = useRef({});

  const [deptMembers, setDeptMembers] = useState([]);
  const [fieldDefaults, setFieldDefaults] = useState({});
  const [lookupMetadata, setLookupMetadata] = useState({});

  
  const loadForms = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forms`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data.filter(f => f.flow && f.fields?.length > 0));
      }
    } catch (error) {
      console.error('Failed to load forms:', error);
    } finally {
      setLoading(false);
    }
  };

  
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingMyRequests, setLoadingMyRequests] = useState(true);
  const [myRequestsApproved, setMyRequestsApproved] = useState([]);
  const [loadingMyRequestsApproved, setLoadingMyRequestsApproved] = useState(true);
  const [selectedRequestDetail, setSelectedRequestDetail] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [possibleDepartment, setPossibleDepartment] = useState([]);

  
  const normalizeRequests = (arr) => (arr || []).map(r => ({ id: r.id, status: r.status, currentNodeLabel: r.currentNodeLabel, isAwaitingMyApproval: !!r.isAwaitingMyApproval, createdAt: r.createdAt })).sort((a, b) => (a.id || '').toString().localeCompare((b.id || '').toString()));
  const isSameRequests = (a, b) => JSON.stringify(normalizeRequests(a)) === JSON.stringify(normalizeRequests(b));

  useEffect(() => {
    loadForms();
    loadPendingApprovals();
    loadMyRequests();
    loadMyRequestsApproved();
    const pInterval = setInterval(() => loadPendingApprovals({ background: true }), 10000);
    const mInterval = setInterval(() => loadMyRequests({ background: true }), 10000);
    const maInterval = setInterval(() => loadMyRequestsApproved({ background: true }), 10000);

    return () => { clearInterval(pInterval); clearInterval(mInterval); clearInterval(maInterval); };
  }, []);

  const loadPendingApprovals = async ({ background = false } = {}) => {
    if (!background) setLoadingPending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/pending-approvals`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (!isSameRequests(data, pendingApprovals)) {
          let pending = data.filter(r => r.status === 'pending' && r.isAwaitingMyApproval);
          setPendingApprovals(pending);
        }
      }
    } catch (error) {
      console.error('Failed to load pending approvals:', error);
    } finally {
      if (!background) setLoadingPending(false);
    }
  };

  const loadMyRequests = async ({ background = false } = {}) => {
    if (!background) setLoadingMyRequests(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/my-requests`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const pending = (data || []).filter(r => r.status === 'pending');
        if (!isSameRequests(pending, myRequests)) {
          setMyRequests(pending);
        }
      }
    } catch (error) {
      console.error('Failed to load my requests:', error);
    } finally {
      if (!background) setLoadingMyRequests(false);
    }
  };

  const loadMyRequestsApproved = async ({ background = false } = {}) => {
    if (!background) setLoadingMyRequestsApproved(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/my-requests`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const approved = (data || []).filter(r => r.status === 'approved');
        if (!isSameRequests(approved, myRequestsApproved)) {
          setMyRequestsApproved(approved);
        }
      }
    } catch (error) {
      console.error('Failed to load my approved requests:', error);
    }
    finally {
      if (!background) setLoadingMyRequestsApproved(false);
    }
  };



  const openRequestDetail = (request) => {
    setSelectedRequestDetail(request);
    setDetailModalOpen(true);
  };

  const closeRequestDetail = () => {
    setSelectedRequestDetail(null);
    setDetailModalOpen(false);
  };

  const getStartEditableFields = (form) => {
    try {
      const flow = form.flow;
      if (!flow || !flow.nodes) return null;
      const startNode = flow.nodes.find(n => n.data && n.data.label === 'Start');
      if (!startNode || !startNode.data.editableFields || startNode.data.editableFields.length === 0) return null;
      return startNode.data.editableFields;
    } catch { return null; }
  };

  const handleFormSelect = (form) => {
    Object.values(uniqueDebounceTimersRef.current).forEach(clearTimeout);
    uniqueDebounceTimersRef.current = {};
    uniqueRequestVersionRef.current = {};
    setUniqueValidation({});
    setSelectedForm(form);
    setFormData({});
    setFieldDefaults({});
    setLookupMetadata({});
    setErrors({});
    setShowModal(true);

    const departmentValue = possibleDepartment[0] || '';
    const defaults = {};
    (form.fields || []).forEach(field => {
      if (field.type === 'date') {
        defaults[field.id] = toDateInputValue(new Date());
      }
      if (field.type === 'datetime') {
        defaults[field.id] = toDateTimeInputValue(new Date());
      }
      if (field.type === 'text' && field.label === 'Department' && departmentValue) {
        defaults[field.id] = departmentValue;
      }
    });
    setFieldDefaults(defaults);
  };

  const handleFieldChange = (field, value) => {
    const fieldId = field.id;
    setFormData({ ...formData, [fieldId]: value });
    if (errors[fieldId]) {
      setErrors({ ...errors, [fieldId]: null });
    }

    if (!field.unique || field.type === 'section' || field.type === 'image' || field.type === 'department_members' || field.type === 'multiselect') {
      setUniqueValidation(prev => {
        if (!prev[fieldId]) return prev;
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
      return;
    }

    if (uniqueDebounceTimersRef.current[fieldId]) {
      clearTimeout(uniqueDebounceTimersRef.current[fieldId]);
    }

    const normalized = typeof value === 'string' ? value.trim() : value;
    if (normalized === null || normalized === undefined || normalized === '') {
      setUniqueValidation(prev => ({
        ...prev,
        [fieldId]: { checking: false, exists: false, message: '' }
      }));
      return;
    }

    const nextVersion = (uniqueRequestVersionRef.current[fieldId] || 0) + 1;
    uniqueRequestVersionRef.current[fieldId] = nextVersion;
    setUniqueValidation(prev => ({
      ...prev,
      [fieldId]: { checking: true, exists: false, message: '' }
    }));

    uniqueDebounceTimersRef.current[fieldId] = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/requests/validate-unique-field`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            formId: selectedForm?.id,
            fieldId,
            value
          })
        });

        if (uniqueRequestVersionRef.current[fieldId] !== nextVersion) return;

        if (!response.ok) {
          setUniqueValidation(prev => ({
            ...prev,
            [fieldId]: { checking: false, exists: false, message: '' }
          }));
          return;
        }

        const data = await response.json();
        if (data.valid === false) {
          setUniqueValidation(prev => ({
            ...prev,
            [fieldId]: { checking: false, exists: true, message: data.message || `this ${field.label || 'field'} already exist` }
          }));
        } else {
          setUniqueValidation(prev => ({
            ...prev,
            [fieldId]: { checking: false, exists: false, message: '' }
          }));
        }
      } catch (e) {
        if (uniqueRequestVersionRef.current[fieldId] !== nextVersion) return;
        setUniqueValidation(prev => ({
          ...prev,
          [fieldId]: { checking: false, exists: false, message: '' }
        }));
      }
    }, 450);
  };

  // fetch department members when form contains that type
  useEffect(() => {
    if (selectedForm && selectedForm.fields.some(f => f.type === 'department_members')) {
      fetch(`${API_BASE_URL}/api/department-members`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : Promise.reject('Failed to load members'))
        .then(setDeptMembers)
        .catch(err => {
          console.error('could not fetch department members', err);
          setDeptMembers([]);
        });
    } else {
      setDeptMembers([]);
    }
  }, [selectedForm]);


  useEffect(()=>{
    function getDepartment(){
      fetch(`${API_BASE_URL}/api/user-department`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : Promise.reject('Failed to load department'))
        .then(data => {
          setPossibleDepartment(data.department ? [data.department] : []);
        })
        .catch(err => {
          console.error('could not fetch user department', err);
        });
    }
    getDepartment();
  },[selectedForm]);

  useEffect(() => {
    if (!selectedForm || !possibleDepartment[0]) return;
    const departmentValue = possibleDepartment[0];
    setFieldDefaults(prev => {
      const next = { ...prev };
      let changed = false;
      (selectedForm.fields || []).forEach(field => {
        if (field.type === 'text' && field.label === 'Department' && next[field.id] !== departmentValue) {
          next[field.id] = departmentValue;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [possibleDepartment, selectedForm]);

  const handleImageChange = (fieldId, file) => {
    setFormData({ ...formData, [fieldId]: file });
    if (errors[fieldId]) {
      setErrors({ ...errors, [fieldId]: null });
    }
  };

  useEffect(() => {
    return () => {
      Object.values(uniqueDebounceTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const getResolvedFieldValue = (field) => {
    const currentValue = formData[field.id];
    if (currentValue !== undefined && currentValue !== null) {
      return currentValue;
    }
    return fieldDefaults[field.id] ?? '';
  };

  const validateForm = () => {
    const newErrors = {};
    const startEditableFields = getStartEditableFields(selectedForm);
    const visibleFields = startEditableFields
      ? selectedForm.fields.filter(f => startEditableFields.includes(f.id))
      : selectedForm.fields;
    visibleFields.forEach(field => {
      if (field.required) {
        const val = getResolvedFieldValue(field);
        const missing = val == null ||
                        (Array.isArray(val) && val.length === 0) ||
                        (typeof val === 'string' && val.trim() === '');
        if (missing) {
          newErrors[field.id] = 'This field is required';
        }
      }
      if (field.unique && uniqueValidation[field.id]?.checking) {
        newErrors[field.id] = 'Validation in progress';
      }
      if (field.unique && uniqueValidation[field.id]?.exists) {
        newErrors[field.id] = uniqueValidation[field.id].message || `this ${field.label || 'field'} already exist`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const resolvedData = {};
      for (const field of selectedForm.fields) {
        resolvedData[field.id] = getResolvedFieldValue(field);
      }
      for (const field of selectedForm.fields) {
        if (field.type === 'image' && resolvedData[field.id] instanceof File) {
          const fd = new FormData();
          fd.append('image', resolvedData[field.id]);
          const upRes = await fetch(`${API_BASE_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: fd
          });
          if (!upRes.ok) { alert('Image upload failed'); setSubmitting(false); return; }
          const { url } = await upRes.json();
          resolvedData[field.id] = url;
        }
      }

      if (Object.keys(lookupMetadata).length > 0) {
        resolvedData.__lookupMetadata = lookupMetadata;
      }

      const response = await fetch(`${API_BASE_URL}/api/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          formId: selectedForm.id,
          formData: resolvedData,
          initiatorId: user.id
        })
      });

      if (response.ok) {
        setShowModal(false);
        setSelectedForm(null);
        setFormData({});
        setFieldDefaults({});
        setLookupMetadata({});
        setUniqueValidation({});
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Failed to submit request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader2 size={48} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="forms-dashboard">
      <div className="dashboard-header">
      </div>

      <div className="dashboard-inline-lists" style={{ display: 'flex', gap: '20px', marginTop: '20px', marginBottom: '20px' }}>
        <div className="mini-card" style={{ flex: 1, padding: '16px', border: '1px solid #e0e4ea', borderRadius: '8px', background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Pending Approvals</h3>
            <div>
              <button
                onClick={() => onNavigate ? onNavigate('employee-approvals') : null}
                className="button-outline"
              >
                View all
              </button>
            </div>
          </div>

          <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '6px' }}>
            {loadingPending ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '140px' }}><Loader2 size={20} className="animate-spin" /></div>
            ) : pendingApprovals.length === 0 ? (
              <div style={{ color: '#64748b', padding: '12px 0' }}>No approvals pending</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pendingApprovals.slice(0,5).map(r => (
                  <div
                    key={r.id}
                    className="mini-list-item"
                    onClick={() => openRequestDetail(r)}
                    style={{ cursor: 'pointer', padding: '8px', borderRadius: '6px', border: '1px solid #e6eef7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.formName}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{r.initiatorName} · {formatLongDate(r.createdAt)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className={`status-badge status-${r.status}`}>{r.status}{r.isAwaitingMyApproval && <Bell size={14} style={{ marginLeft: 6 }} />}</span>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{r.currentNodeLabel || 'Completed'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mini-card" style={{ flex: 1, padding: '16px', border: '1px solid #e0e4ea', borderRadius: '8px', background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>My Pending Requests</h3>
            <div>
              <button
                onClick={() => onNavigate ? onNavigate('employee-requests') : null}
                className="button-outline"
              >
                View all
              </button>
            </div>
          </div>

          <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '6px' }}>
            {loadingMyRequests ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '140px' }}><Loader2 size={20} className="animate-spin" /></div>
            ) : myRequests.length === 0 ? (
              <div style={{ color: '#64748b', padding: '12px 0' }}>No pending requests</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {myRequests.slice(0,5).map(r => (
                  <div
                    key={r.id}
                    className="mini-list-item"
                    onClick={() => openRequestDetail(r)}
                    style={{ cursor: 'pointer', padding: '8px', borderRadius: '6px', border: '1px solid #e6eef7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.formName}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{formatLongDate(r.createdAt)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className={`status-badge status-${r.status}`}>{r.status}</span>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{r.currentNodeLabel || 'Completed'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mini-card" style={{ flex: 1, padding: '16px', border: '1px solid #e0e4ea', borderRadius: '8px', background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Recently Approved Requests</h3>
            <div>
              <button
                onClick={() => onNavigate ? onNavigate('employee-requests') : null}
                className="button-outline"
              >
                View all
              </button>
            </div>
          </div>

          <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '6px' }}>
            {loadingMyRequestsApproved ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '140px' }}><Loader2 size={20} className="animate-spin" /></div>
            ) : myRequestsApproved.length === 0 ? (
              <div style={{ color: '#64748b', padding: '12px 0' }}>No recently approved requests</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {myRequestsApproved.slice(0,5).map(r => (
                  <div
                    key={r.id}
                    className="mini-list-item"
                    onClick={() => openRequestDetail(r)}
                    style={{ cursor: 'pointer', padding: '8px', borderRadius: '6px', border: '1px solid #e6eef7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.formName}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{formatLongDate(r.createdAt)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className={`status-badge status-${r.status}`}>{r.status}</span>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{r.currentNodeLabel || 'Completed'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        



      </div>

      <div className="request-section">
        <div className="request-section-header">
          <div className="dashboard-title">
            <h1>Initiate Request</h1>
            <p className="dashboard-subtitle">Select a form to start a new approval request</p>
          </div>
          {forms.length > 0 && (
            <div className="request-search-wrap">
              <Search size={16} className="request-search-icon" />
              <input
                type="text"
                className="request-search-input"
                placeholder="Search forms..."
                value={formSearch}
                onChange={(e) => setFormSearch(e.target.value)}
              />
            </div>
          )}
        </div>

        {forms.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} />
            <h3>No forms available</h3>
            <p>There are no forms with approval workflows configured yet</p>
          </div>
        ) : (
          (() => {
            const query = formSearch.trim().toLowerCase();
            const filteredForms = query
              ? forms.filter(form =>
                  form.name?.toLowerCase().includes(query) ||
                  form.description?.toLowerCase().includes(query)
                )
              : forms;

            if (filteredForms.length === 0) {
              return (
                <div className="empty-state">
                  <Search size={40} />
                  <h3>No matching forms</h3>
                  <p>Try a different search term</p>
                </div>
              );
            }

            return (
              <div className="request-forms-list">
                {filteredForms.map(form => (
                  <div key={form.id} className="request-form-row" onClick={() => handleFormSelect(form)}>
                    <div className="request-form-icon">
                      <FileText size={18} />
                    </div>
                    <div className="request-form-info">
                      <div className="request-form-name">{form.name}</div>
                      <div className="request-form-desc">{form.description || 'No description'}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFormSelect(form);
                      }}
                      className="request-form-start"
                    >
                      Start <ChevronRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
            );
          })()
        )}
      </div>

      <Modal
        isOpen={detailModalOpen}
        onClose={closeRequestDetail}
        title={selectedRequestDetail?.formName || 'Request Details'}
        size="large"
      >
        {selectedRequestDetail && (
          <RequestDetail
            request={selectedRequestDetail}
            user={user}
            onUpdate={() => { loadPendingApprovals({ background: true }); loadMyRequests({ background: true }); }}
            isApprover={!!selectedRequestDetail.isAwaitingMyApproval}
            isInitiator={selectedRequestDetail?.initiatorId === user.id}
          />
        )}
      </Modal>

      <FullScreenPanel
        isOpen={showModal}
        onClose={() => {
          Object.values(uniqueDebounceTimersRef.current).forEach(clearTimeout);
          uniqueDebounceTimersRef.current = {};
          uniqueRequestVersionRef.current = {};
          setShowModal(false);
          setSelectedForm(null);
          setFormData({});
          setLookupMetadata({});
          setUniqueValidation({});
          setErrors({});
        }}
        title={selectedForm?.name || 'Submit Request'}
        subtitle="New Request"
      >
        {selectedForm && (
          <form onSubmit={handleSubmit} onKeyDown={preventImplicitSubmit}>
            <div style={{ marginBottom: '24px' }}>
              <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6' }}>
                {selectedForm.description}
              </p>
            </div>

            {(getStartEditableFields(selectedForm)
              ? selectedForm.fields.filter(f => getStartEditableFields(selectedForm).includes(f.id))
              : selectedForm.fields
            ).map(field => {
              if (field.type === 'section') {
                return (
                  <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                    <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{field.label || 'Section'}</span>
                    <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                  </div>
                );
              }
              return (
              <div key={field.id} className="form-field" style={{ marginBottom: '20px' }}>
                <label className="form-field-label">
                  {field.label}
                  {field.required && <span style={{ color: '#dc2626' }}> *</span>}
                </label>

                {field.type === 'text' && field.label !== "Department" && !field.lookup?.datasetId && (

                  <input
                    type="text"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength || undefined}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />

                )}

                {field.type === 'text' && field.label !== "Department" && field.lookup?.datasetId && (
                  <>
                    <LookupSuggest
                      datasetId={field.lookup.datasetId}
                      mode={field.lookup.mode || 'warn'}
                      value={getResolvedFieldValue(field)}
                      placeholder={field.placeholder}
                      inputClassName={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                      onChangeText={(v) => {
                        handleFieldChange(field, v);
                        if (field.lookup.mode !== 'select') {
                          setLookupMetadata(prev => {
                            if (!prev[field.id]) return prev;
                            const n = { ...prev }; delete n[field.id]; return n;
                          });
                        }
                      }}
                      onSelect={(item) => {
                        if ((field.lookup.mode || 'warn') === 'select') {
                          handleFieldChange(field, item.value);
                          setLookupMetadata(prev => ({ ...prev, [field.id]: { label: item.label, metadata: item.metadata, value: item.value } }));
                        }
                      }}
                    />
                    {(field.lookup.mode || 'warn') === 'select' && lookupMetadata[field.id] && (
                      <MetadataCard items={lookupMetadata[field.id].metadata} title={lookupMetadata[field.id].label} />
                    )}
                  </>
                )}

                {field.type === 'text' && field.label === "Department" && (
                 
                  <input 
                    type="text"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={field.placeholder}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />
                )

                }

                {field.type === 'email' && (
                  <input
                    type="email"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength || undefined}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />
                )}

                {field.type === 'textarea' && (
                  <textarea
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={field.placeholder}
                    rows={4}
                    maxLength={field.maxLength || undefined}
                    className={`form-field-textarea ${errors[field.id] ? 'error' : ''}`}
                  />
                )}

                {field.type === 'number' && (
                  <input
                    type="number"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={field.placeholder}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />
                )}
                {field.type === 'date' && (
                  <input
                    type="date"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />
                )}

                {field.type === 'datetime' && (
                  <input
                    type="datetime-local"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />
                )}

                {field.type === 'time' && (
                  <input
                    type="time"
                    value={getResolvedFieldValue(field)}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    className={`form-field-input ${errors[field.id] ? 'error' : ''}`}
                  />
                )}

                {field.type === 'select' && (
                  <SearchableSelect
                    value={getResolvedFieldValue(field)}
                    onChange={(v) => handleFieldChange(field, v)}
                    options={field.options || []}
                    placeholder="Select an option"
                    className={`form-field-select ${errors[field.id] ? 'error' : ''}`}
                  />
                )}

                {field.type === 'multiselect' && (
                  <div className="field-checkbox-group">
                    {(field.options || []).map((opt, idx) => {
                      const selected = Array.isArray(formData[field.id]) ? formData[field.id] : [];
                      const checked = selected.includes(opt);
                      return (
                        <div key={idx} className="field-checkbox">
                          <input
                            type="checkbox"
                            id={`${field.id}-${idx}`}
                            checked={checked}
                            onChange={e => {
                              const prev = Array.isArray(formData[field.id]) ? [...formData[field.id]] : [];
                              if (e.target.checked) prev.push(opt);
                              else {
                                const i = prev.indexOf(opt);
                                if (i !== -1) prev.splice(i, 1);
                              }
                              handleFieldChange(field, prev);
                            }}
                          />
                          <label htmlFor={`${field.id}-${idx}`}>{opt}</label>
                        </div>
                      );
                    })}
                  </div>
                )}

                {field.type === 'image' && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (file) handleImageChange(field.id, file);
                      }}
                      className={errors[field.id] ? 'error' : ''}
                    />
                    {formData[field.id] instanceof File && (
                      <div style={{ fontSize: 12, color: '#0369a1', marginTop: 4 }}>{formData[field.id].name}</div>
                    )}
                  </div>
                )}

                {field.type === 'department_members' && (
                  <div>
                    {deptMembers.length === 0 ? (
                      <div>Loading department members...</div>
                    ) : (
                      deptMembers.map(m => {
                        const selected = Array.isArray(formData[field.id]) ? formData[field.id] : [];
                        const checked = selected.some(s => (typeof s === 'object' ? s.code : s) === m.Empcode);
                        return (
                          <div key={m.Empcode} className="field-checkbox">
                            <input
                              type="checkbox"
                              id={`${field.id}-${m.Empcode}`}
                              checked={checked}
                              onChange={e => {
                                const prev = Array.isArray(formData[field.id]) ? [...formData[field.id]] : [];
                                if (e.target.checked) prev.push({ code: m.Empcode, name: m.EmployeeName });
                                else {
                                  const idx = prev.findIndex(s => (typeof s === 'object' ? s.code : s) === m.Empcode);
                                  if (idx !== -1) prev.splice(idx, 1);
                                }
                                handleFieldChange(field, prev);
                              }}
                            />
                            <label htmlFor={`${field.id}-${m.Empcode}`}>{m.EmployeeName}</label>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {field.maxLength > 0 && (field.type === 'text' || field.type === 'textarea' || field.type === 'email') && (
                  <div style={{ marginTop: 4, fontSize: 12, textAlign: 'right', color: String(getResolvedFieldValue(field) || '').length >= field.maxLength ? '#dc2626' : '#94a3b8' }}>
                    {String(getResolvedFieldValue(field) || '').length} / {field.maxLength}
                  </div>
                )}

                {field.unique && uniqueValidation[field.id]?.checking && (
                  <div style={{ marginTop: 6, color: '#334155', fontSize: 13 }}>
                    Checking {field.label || 'field'}...
                  </div>
                )}

                {field.unique && uniqueValidation[field.id]?.exists && (
                  <div className="field-error">{uniqueValidation[field.id].message || `this ${field.label || 'field'} already exist`}</div>
                )}

                {errors[field.id] && (
                  <div className="field-error">{errors[field.id]}</div>
                )}
              </div>
            );
            })}

            <div className="submission-actions">
              <button
                type="button"
                onClick={() => {
                  Object.values(uniqueDebounceTimersRef.current).forEach(clearTimeout);
                  uniqueDebounceTimersRef.current = {};
                  uniqueRequestVersionRef.current = {};
                  setShowModal(false);
                  setSelectedForm(null);
                  setFormData({});
                  setFieldDefaults({});
                  setLookupMetadata({});
                  setUniqueValidation({});
                  setErrors({});
                }}
                className="button-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="button-primary"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        )}
      </FullScreenPanel>
    </div>
  );
}
